import { useState, useEffect } from 'react';
import { onSnapshot, query, collection, orderBy, limit, getCountFromServer } from 'firebase/firestore';
import { db } from '../config/firebase';

export const useStats = () => {
  const [stats, setStats] = useState({
    communiques: 0,
    articles: 0,
    conflits: 0,
    galerie: 0,
    views: 0,
    loading: true,
    error: null
  });

  const [recentActivities, setRecentActivities] = useState([]);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Compter les communiqués
        const communiqueColl = collection(db, 'communiques');
        const communiqueSnapshot = await getCountFromServer(communiqueColl);
        const communiquesCount = communiqueSnapshot.data().count;

        // Compter les articles
        const articlesColl = collection(db, 'articles');
        const articlesSnapshot = await getCountFromServer(articlesColl);
        const articlesCount = articlesSnapshot.data().count;

        // Compter les conflits
        const conflitsColl = collection(db, 'conflits');
        const conflitsSnapshot = await getCountFromServer(conflitsColl);
        const conflitsCount = conflitsSnapshot.data().count;

        // Compter les photos de la galerie
        const galerieColl = collection(db, 'galerie');
        const galerieSnapshot = await getCountFromServer(galerieColl);
        const galerieCount = galerieSnapshot.data().count;

        setStats({
          communiques: communiquesCount,
          articles: articlesCount,
          conflits: conflitsCount,
          galerie: galerieCount,
          views: Math.floor(Math.random() * 5000) + 1000, // Simulé pour l'instant
          loading: false,
          error: null
        });
      } catch (err) {
        console.error('Erreur stats:', err);
        setStats(prev => ({
          ...prev,
          loading: false,
          error: err.message
        }));
      }
    };

    fetchCounts();

    // Écouter les changements en temps réel
    const unsubCommuniques = onSnapshot(collection(db, 'communiques'), () => fetchCounts());
    const unsubArticles = onSnapshot(collection(db, 'articles'), () => fetchCounts());
    const unsubConflits = onSnapshot(collection(db, 'conflits'), () => fetchCounts());
    const unsubGalerie = onSnapshot(collection(db, 'galerie'), () => fetchCounts());

    return () => {
      unsubCommuniques();
      unsubArticles();
      unsubConflits();
      unsubGalerie();
    };
  }, []);

  // Charger les activités récentes
  useEffect(() => {
    const activities = [];

    // Écouter les derniers communiqués
    const qCommuniques = query(collection(db, 'communiques'), orderBy('date', 'desc'), limit(3));
    const unsubCommuniques = onSnapshot(qCommuniques, (snapshot) => {
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: `comm-${doc.id}`,
          title: `Communiqué publié : "${data.title.substring(0, 40)}..."`,
          time: data.date.toDate ? data.date.toDate() : new Date(data.date),
          type: 'communique'
        });
      });
      updateActivities();
    });

    // Écouter les derniers articles
    const qArticles = query(collection(db, 'articles'), orderBy('date', 'desc'), limit(3));
    const unsubArticles = onSnapshot(qArticles, (snapshot) => {
      snapshot.docs.forEach(doc => {
        const data = doc.data();
        activities.push({
          id: `art-${doc.id}`,
          title: `Article publié : "${data.title.substring(0, 40)}..."`,
          time: data.date.toDate ? data.date.toDate() : new Date(data.date),
          type: 'article'
        });
      });
      updateActivities();
    });

    const updateActivities = () => {
      // Trier par date décroissante et prendre les 5 plus récentes
      const sorted = activities
        .sort((a, b) => b.time - a.time)
        .slice(0, 5)
        .map(a => ({
          ...a,
          time: formatTimeAgo(a.time)
        }));
      setRecentActivities(sorted);
    };

    return () => {
      unsubCommuniques();
      unsubArticles();
    };
  }, []);

  return { stats, recentActivities };
};

// Formater le temps écoulé
function formatTimeAgo(date) {
  const now = new Date();
  const diffMs = now - date;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'À l\'instant';
  if (diffMins < 60) return `Il y a ${diffMins} minute${diffMins > 1 ? 's' : ''}`;
  if (diffHours < 24) return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  if (diffDays < 7) return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  return date.toLocaleDateString('fr-FR');
}
